/**
 * #628 (increment B) — client-side "reconnect to Genesys" store.
 *
 * @dylan219 reported ONLINE journeys failing with RBAC permission denials
 * despite correct agent profiles: the Genesys↔Webapp online-session link (the
 * pinned/claimed interaction id) can DROP mid-call, so the online state resolves
 * Offline server-side and online-only RBAC actions are then refused with a 403.
 *
 * The BFF marks that specific case on the 403 body (`onlineWouldAllow: true`,
 * plus a fixed `offlineReason` category — see the RBAC permission middleware and
 * the OpenAPI `Error` schema). A single openapi-fetch middleware (wired in
 * `api/client.ts`) captures that signal into this tiny observable store, so the
 * app can render a clear, actionable "Reconnect to Genesys" affordance the moment
 * ANY online-only action is refused for this reason — rather than a bare
 * permission error the agent can't act on.
 *
 * SECURITY: this store is purely a UX signal. It never grants online state. The
 * reconnect it prompts re-pins the (mount-frozen) interaction id through the
 * EXISTING `POST /api/genesys/session-context` route, which runs the SAME
 * server-side #270 participant validation (`checkConversationParticipation` in
 * `genesys/conversations.ts`, resolving the agent via Genesys `/users/me`) before
 * the session resolves Online again. A failed re-validation stays Offline — the
 * store can never be the thing that "unblocks" the agent.
 */

/** The recoverable online-only-Offline denial captured off a 403 body. */
export interface ReconnectNeeded {
  /** The fixed diagnostic reason category the state resolved Offline. */
  readonly offlineReason: string;
}

type Listener = (state: ReconnectNeeded | null) => void;

let current: ReconnectNeeded | null = null;
const listeners = new Set<Listener>();

/** The currently-active reconnect prompt, or null when none. */
export function getReconnectNeeded(): ReconnectNeeded | null {
  return current;
}

/** Record (or clear) the reconnect prompt and notify subscribers. */
export function setReconnectNeeded(state: ReconnectNeeded | null): void {
  current = state;
  for (const l of listeners) l(state);
}

/** Subscribe to reconnect-prompt changes; returns an unsubscribe fn. */
export function subscribeReconnectNeeded(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The permission-403 body shape the BFF returns (see OpenAPI `Error`). */
interface ForbiddenBody {
  error?: string;
  onlineWouldAllow?: boolean;
  offlineReason?: string;
}

/**
 * Inspect a fetched Response for the #628 online-only-Offline signal and, if
 * present, record the reconnect prompt. Clones the response (bodies are one-shot)
 * so the caller can still read it. A no-op for any response that is not a 403
 * carrying `onlineWouldAllow: true`.
 */
export async function noteReconnectNeededFrom(
  response: Response,
): Promise<void> {
  if (response.status !== 403) return;
  try {
    const body = (await response.clone().json()) as ForbiddenBody;
    if (body?.onlineWouldAllow === true) {
      setReconnectNeeded({ offlineReason: body.offlineReason ?? 'unknown' });
    }
  } catch {
    /* non-JSON 403 — ignore */
  }
}
